import React, { useEffect, useMemo, useState } from 'react';
import MacroSeriesChart from './MacroSeriesChart';
import { fetchMacroSeries } from '../utils/bondMacroApi';
import './BondDashboard.css';

function InflationRevaluationPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eaHicpRate, setEaHicpRate] = useState(null);
  const [eaIndex, setEaIndex] = useState(null);
  const [itIndex, setItIndex] = useState(null);
  const [btpei, setBtpei] = useState({
    baseIndex: '',
    currentIndex: '',
    notional: '100000',
    deflationFloor: true,
  });
  const [btpItalia, setBtpItalia] = useState({
    baseIndex: '',
    currentIndex: '',
    notional: '100000',
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [eaIdx, eaRate, it] = await Promise.all([
          fetchMacroSeries('EA_HICP_IDX', { days: 120 }),
          fetchMacroSeries('EA_HICP', { days: 120 }),
          fetchMacroSeries('IT_HICP_IDX', { days: 120 }),
        ]);
        if (cancelled) return;
        setEaIndex(eaIdx);
        setEaHicpRate(eaRate);
        setItIndex(it);
        const latestEaIdx = eaIdx.latest?.value;
        const latestItIdx = it.latest?.value;
        setBtpei((prev) => ({
          ...prev,
          currentIndex:
            prev.currentIndex || (latestEaIdx != null ? String(latestEaIdx) : ''),
        }));
        setBtpItalia((prev) => ({
          ...prev,
          currentIndex:
            prev.currentIndex || (latestItIdx != null ? String(latestItIdx) : ''),
        }));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load inflation series');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const btpeiResult = useMemo(() => {
    const base = Number(btpei.baseIndex);
    const current = Number(btpei.currentIndex);
    const notional = Number(btpei.notional);
    if (!base || !current || !notional) return null;
    const factor = Math.max(btpei.deflationFloor ? 1 : 0, current / base);
    return {
      factor,
      revalued: notional * factor,
      upliftPct: (factor - 1) * 100,
    };
  }, [btpei]);

  const italiaResult = useMemo(() => {
    const base = Number(btpItalia.baseIndex);
    const current = Number(btpItalia.currentIndex);
    const notional = Number(btpItalia.notional);
    if (!base || !current || !notional) return null;
    const factor = current / base;
    return {
      factor,
      revalued: notional * factor,
      upliftPct: (factor - 1) * 100,
    };
  }, [btpItalia]);

  const eaRateLabel =
    eaHicpRate?.latest?.value != null
      ? `${Number(eaHicpRate.latest.value).toFixed(2)}% y/y`
      : null;

  return (
    <section className="bond-dash-panel bond-dash-panel--fill">
      <div className="bond-dash-header">
        <h3 className="bond-dash-title">Inflation-Linked Revaluation Tracker</h3>
        <p className="bond-dash-subtitle">
          BTP€i: euro-area HICP index levels (reference at issue vs current). BTP Italia: Italy HICP
          index proxy (use FOI ex-tobacco from ISTAT for official terms). Lite revaluation =
          (current ÷ reference) × notional — issue-specific coefficients still apply on real bonds.
        </p>
      </div>

      <div className="bond-dash-body">
        {loading && <div className="bond-dash-loading">Loading inflation indices…</div>}
        {error && <div className="bond-dash-error">{error}</div>}

        {!loading && !error && (
          <>
            <div className="bond-dash-grid-2">
              <div className="bond-dash-card">
                <h4 className="bond-dash-card-title">Euro-area HICP index (2015=100) — BTP€i</h4>
                <div className="bond-dash-stat">
                  {eaIndex?.latest?.value != null
                    ? Number(eaIndex.latest.value).toFixed(1)
                    : '—'}
                </div>
                {eaRateLabel && (
                  <div className="bond-dash-stat-label" style={{ marginBottom: 8 }}>
                    Annual rate (context only): {eaRateLabel}
                  </div>
                )}
                <MacroSeriesChart
                  bars={eaIndex?.bars || []}
                  color="#1F4E8C"
                  title="EA HICP idx"
                  height={200}
                />
              </div>

              <div className="bond-dash-card">
                <h4 className="bond-dash-card-title">Italy HICP index (2015=100) — BTP Italia</h4>
                <div className="bond-dash-stat">
                  {itIndex?.latest?.value != null
                    ? Number(itIndex.latest.value).toFixed(1)
                    : '—'}
                </div>
                <MacroSeriesChart
                  bars={itIndex?.bars || []}
                  color="#A16D17"
                  title="IT HICP idx"
                  height={200}
                />
              </div>
            </div>

            <div className="bond-dash-grid-2">
              <div className="bond-dash-card">
                <h4 className="bond-dash-card-title">BTP€i lite revaluation</h4>
                <p className="bond-dash-stat-label" style={{ marginTop: 0, marginBottom: 10 }}>
                  Enter index levels, not the % annual inflation rate.
                </p>
                <div className="bond-dash-form-row">
                  <input
                    className="bond-dash-input"
                    type="number"
                    step="any"
                    placeholder="Reference index (issue)"
                    value={btpei.baseIndex}
                    onChange={(e) => setBtpei((s) => ({ ...s, baseIndex: e.target.value }))}
                  />
                  <input
                    className="bond-dash-input"
                    type="number"
                    step="any"
                    placeholder="Current index"
                    value={btpei.currentIndex}
                    onChange={(e) => setBtpei((s) => ({ ...s, currentIndex: e.target.value }))}
                  />
                  <input
                    className="bond-dash-input"
                    type="number"
                    placeholder="Notional €"
                    value={btpei.notional}
                    onChange={(e) => setBtpei((s) => ({ ...s, notional: e.target.value }))}
                  />
                </div>
                <label style={{ fontSize: 12, color: '#4A5672', marginTop: 8, display: 'block' }}>
                  <input
                    type="checkbox"
                    checked={btpei.deflationFloor}
                    onChange={(e) => setBtpei((s) => ({ ...s, deflationFloor: e.target.checked }))}
                  />
                  {' '}Apply deflation floor (factor ≥ 1)
                </label>
                {btpeiResult && (
                  <div style={{ marginTop: 12 }}>
                    <div className="bond-dash-result">
                      €{btpeiResult.revalued.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>
                    <div className="bond-dash-stat-label">
                      Factor {btpeiResult.factor.toFixed(4)} · uplift {btpeiResult.upliftPct.toFixed(2)}%
                    </div>
                  </div>
                )}
              </div>

              <div className="bond-dash-card">
                <h4 className="bond-dash-card-title">BTP Italia lite revaluation</h4>
                <div className="bond-dash-form-row">
                  <input
                    className="bond-dash-input"
                    type="number"
                    step="any"
                    placeholder="Reference index (issue)"
                    value={btpItalia.baseIndex}
                    onChange={(e) => setBtpItalia((s) => ({ ...s, baseIndex: e.target.value }))}
                  />
                  <input
                    className="bond-dash-input"
                    type="number"
                    step="any"
                    placeholder="Current index"
                    value={btpItalia.currentIndex}
                    onChange={(e) => setBtpItalia((s) => ({ ...s, currentIndex: e.target.value }))}
                  />
                  <input
                    className="bond-dash-input"
                    type="number"
                    placeholder="Notional €"
                    value={btpItalia.notional}
                    onChange={(e) => setBtpItalia((s) => ({ ...s, notional: e.target.value }))}
                  />
                </div>
                {italiaResult && (
                  <div style={{ marginTop: 12 }}>
                    <div className="bond-dash-result">
                      €{italiaResult.revalued.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>
                    <div className="bond-dash-stat-label">
                      Factor {italiaResult.factor.toFixed(4)} · uplift {italiaResult.upliftPct.toFixed(2)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default InflationRevaluationPanel;
